import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../EditableSection';
import InputField from '../ui/InputField';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateInvoiceDispatchEmailMutation } from '../../api/account/accountApiSlice';
import { useInvoiceMasterSlug } from '../../hooks/useIsInvoiceIssuingTenant';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    /** The tenant's ADR-0012 mode. Only `kolula` issues, so only it dispatches. */
    invoiceIssuer: string | null | undefined;
    /** The stored address, or null when unset. */
    invoiceDispatchEmail: string | null;
    /** No edit rights: the view only, no "Bearbeiten". */
    readOnly?: boolean;
}

/** The address is saved trimmed, so whitespace alone is not a change. */
const sameAddress = (a: string, b: string) => a.trim() === b.trim();

/**
 * Where issued invoices are mailed when the booking's customer is not the
 * recipient — typically the tenant's own accounting inbox.
 *
 * Precedence level 2 of invoice dispatch: the customer organisation's own
 * invoice address wins where it exists, this comes next, and the person who
 * booked is the fallback. Empty means "fall through", never "do not send", so
 * clearing it is safe and is how a tenant goes back to mailing the customer
 * directly.
 *
 * It exists because that is what the lead customer does today — the invoice
 * goes to their own back office, which forwards or files it — and because
 * without a way to set the key, the precedence level would exist in code and
 * be unreachable from the product.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern): the view
 * shows the stored address, "Bearbeiten" opens a draft of it.
 */
const InvoiceDispatchEmailSection = ({
    invoiceIssuer, invoiceDispatchEmail, readOnly = false,
}: Readonly<Props>) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const masterSlug = useInvoiceMasterSlug();
    const [save, { isLoading: saving }] = useUpdateInvoiceDispatchEmailMutation();
    const draft = useEditableDraft<string>(invoiceDispatchEmail ?? '', sameAddress);
    const [error, setError] = useState<string | null>(null);

    if (invoiceIssuer !== 'kolula') return null;

    const handleSave = async () => {
        setError(null);
        const email = draft.value;
        try {
            await save({
                invoiceDispatchEmail: email.trim() === '' ? null : email.trim(),
                ...(masterSlug ? { slug: masterSlug } : {}),
            }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err: unknown) {
            setError(errorMessageFrom(err, t('account.invoice_dispatch_save_failed'), t));
        }
    };

    /** The address — locked in the view, editable in the draft. */
    const renderAddress = (locked: boolean) => (
        <InputField
            label={t('account.invoice_dispatch_email')}
            type={'email'}
            value={locked ? (invoiceDispatchEmail ?? '') : draft.value}
            onChange={draft.setDraft}
            disabled={locked || saving}
        />
    );

    return (
        <EditableSection
            title={t('account.invoice_dispatch_section')}
            description={t('account.invoice_dispatch_section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            error={error}
            onEdit={draft.startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void handleSave(); }}
            view={renderAddress(true)}
        >
            {renderAddress(false)}
        </EditableSection>
    );
};

export default InvoiceDispatchEmailSection;
